/**
 * QR Code Scanner Modal Component
 * Scan asset QR codes for quick lookup
 */
import React, { useState, useEffect } from 'react';
import { X, Camera, QrCode, Package, AlertCircle, CheckCircle } from 'lucide-react';
import { Scanner } from '@yudiel/react-qr-scanner';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getToken = () => localStorage.getItem('token');

const apiCall = async (endpoint, options = {}) => {
  const token = getToken();
  const response = await fetch(`${API_URL}${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      ...options.headers,
    },
  });
  
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Request failed' }));
    throw new Error(error.detail || 'Request failed');
  }
  
  return response.json();
};

// Scanned Asset Display Component
const ScannedAssetCard = ({ asset, onViewDetails, onClose }) => {
  return (
    <div className="scanned-asset-card">
      <div className="scan-success-header">
        <CheckCircle size={24} />
        <span>Asset Found!</span>
      </div>
      
      <div className="asset-card-body">
        <div className="asset-main-info">
          <code className="asset-tag">{asset.asset_tag}</code>
          <h4>{asset.name}</h4>
        </div>
        
        <div className="asset-details-grid">
          <div className="detail-item">
            <span className="label">Category</span>
            <span className={`category-badge cat-${asset.category}`}>{asset.category}</span>
          </div>
          <div className="detail-item">
            <span className="label">Status</span>
            <span className={`status-badge status-${asset.status}`}>{asset.status?.replace('_', ' ')}</span>
          </div>
          <div className="detail-item">
            <span className="label">Condition</span>
            <span className={`condition-badge cond-${asset.condition}`}>{asset.condition}</span>
          </div>
          {asset.serial_number && (
            <div className="detail-item">
              <span className="label">Serial #</span>
              <span className="value mono">{asset.serial_number}</span>
            </div>
          )}
          {asset.assigned_to_name && (
            <div className="detail-item full-width">
              <span className="label">Assigned To</span>
              <span className="value">{asset.assigned_to_name}</span>
            </div>
          )}
          {asset.location && (
            <div className="detail-item full-width">
              <span className="label">Location</span>
              <span className="value">{asset.location}</span>
            </div>
          )}
        </div>
      </div>
      
      <div className="asset-card-actions">
        <button className="btn-secondary" onClick={onClose}>
          Scan Another
        </button>
        <button className="btn-primary" onClick={() => onViewDetails(asset.id)}>
          View Full Details
        </button>
      </div>
    </div>
  );
};

// Main QR Scanner Modal Component
const QRScannerModal = ({ isOpen, onClose, onAssetFound }) => {
  const [scanning, setScanning] = useState(true);
  const [scannedAsset, setScannedAsset] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [cameraError, setCameraError] = useState(null);
  
  useEffect(() => {
    if (isOpen) {
      setScanning(true);
      setScannedAsset(null);
      setError(null);
      setCameraError(null);
    }
  }, [isOpen]);
  
  const handleScan = async (result) => {
    if (!result || !result.length || loading) return;
    
    const scannedText = result[0].rawValue;
    console.log('Scanned:', scannedText);
    
    // Extract asset tag from QR code (format: ASSET:AST-XXXX-XXXXXX)
    let assetTag = scannedText;
    if (scannedText.startsWith('ASSET:')) {
      assetTag = scannedText.replace('ASSET:', '');
    }
    
    setLoading(true);
    setScanning(false);
    setError(null);
    
    try {
      const asset = await apiCall(`/api/it-assets/lookup/${encodeURIComponent(assetTag)}`);
      setScannedAsset(asset);
      toast.success('Asset found!');
    } catch (err) {
      setError(`Asset not found: ${assetTag}`);
      toast.error('Asset not found');
      // Reset to scanning after showing error
      setTimeout(() => {
        setScanning(true);
        setError(null);
      }, 3000);
    } finally {
      setLoading(false);
    }
  };
  
  const handleError = (err) => {
    console.error('Scanner error:', err);
    if (err.name === 'NotAllowedError') {
      setCameraError('Camera access denied. Please allow camera access to scan QR codes.');
    } else if (err.name === 'NotFoundError') {
      setCameraError('No camera found on this device.');
    } else {
      setCameraError('Unable to access camera. Please check permissions.');
    }
  };
  
  const handleViewDetails = (assetId) => {
    if (onAssetFound) {
      onAssetFound(assetId);
    }
    onClose();
  };
  
  const resetScanner = () => {
    setScannedAsset(null);
    setError(null);
    setScanning(true);
  };
  
  if (!isOpen) return null;
  
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content qr-scanner-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title-row">
            <QrCode size={18} />
            <h3>Scan Asset QR Code</h3>
          </div>
          <button className="modal-close" onClick={onClose}><X size={18} /></button>
        </div>
        
        <div className="modal-body scanner-body">
          {cameraError ? (
            <div className="camera-error">
              <AlertCircle size={48} />
              <p>{cameraError}</p>
              <div className="manual-entry" style={{marginTop: '20px'}}>
                <span>Enter asset tag manually:</span>
                <ManualTagInput onSubmit={handleScan} />
              </div>
              <button className="btn-secondary" onClick={onClose} style={{marginTop: '15px'}}>Close</button>
            </div>
          ) : loading ? (
            <div className="scanning-loading">
              <div className="spinner"></div>
              <p>Looking up asset...</p>
            </div>
          ) : scannedAsset ? (
            <ScannedAssetCard 
              asset={scannedAsset}
              onViewDetails={handleViewDetails}
              onClose={resetScanner}
            />
          ) : error ? (
            <div className="scan-error">
              <AlertCircle size={48} />
              <p>{error}</p>
              <p className="hint">Scanning will resume automatically...</p>
            </div>
          ) : scanning ? (
            <div className="scanner-container">
              <div className="scanner-instructions">
                <Camera size={20} />
                <p>Point your camera at an asset QR code</p>
              </div>
              
              <div className="scanner-viewport">
                <Scanner
                  onScan={handleScan}
                  onError={handleError}
                  constraints={{
                    facingMode: 'environment'
                  }}
                  styles={{
                    container: {
                      width: '100%',
                      height: '300px',
                      borderRadius: '8px',
                      overflow: 'hidden'
                    },
                    video: {
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover'
                    }
                  }}
                  components={{
                    audio: false,
                    torch: true
                  }}
                />
                <div className="scanner-overlay">
                  <div className="scan-frame"></div>
                </div>
              </div>
              
              <div className="manual-entry">
                <span>Or enter asset tag manually:</span>
                <ManualTagInput onSubmit={handleScan} />
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

// Manual Tag Input Component
const ManualTagInput = ({ onSubmit }) => {
  const [tag, setTag] = useState('');
  
  const handleSubmit = (e) => {
    e.preventDefault();
    if (tag.trim()) {
      onSubmit([{ rawValue: tag.trim() }]);
      setTag('');
    }
  };
  
  return (
    <form onSubmit={handleSubmit} className="manual-tag-form">
      <input
        type="text"
        value={tag}
        onChange={e => setTag(e.target.value)}
        placeholder="AST-XXXX-XXXXXX"
        className="tag-input"
      />
      <button type="submit" className="btn-small" disabled={!tag.trim()}>
        Lookup
      </button>
    </form>
  );
};

export default QRScannerModal;
